import { isDefined, fetchData } from '@automator/utilities';

/**
 * Class representing the Dashboard Credits functionality.
 */
export default class DashboardCredits {
	/**
	 * Creates an instance of DashboardCredits.
	 * Initializes data and fetches credits and recipes using credits on load.
	 */
	constructor() {
		// Set data
		this.data = {};

		// Get credits left on load
		this.getCreditsLeft();

		// Get recipes using credits on load
		this.getRecipesUsingCredits();
	}

	/**
	 * Fetches and sets the total credits and credits left from the server.
	 *
	 * If the site is connected and the user is not a Pro user, this function sends a request to the server to get the total credits and credits left. On success, it sets the `totalCredits` and `creditsLeft` properties with the response data.
	 */
	getCreditsLeft() {
		// Check if we should get the credits
		if ( UncannyAutomatorBackend.hasSiteConnected && ! UncannyAutomatorBackend.isPro ) {
			// Do a request to get the credits left
			fetchData( {
				isWordPressREST: true,
				url: `${UncannyAutomatorBackend.rest.url}/get-credits/`,
				onSuccess: ( response ) => {
					// Set total credits
					this.totalCredits = response.total_credits;

					// Set credits left
					this.creditsLeft = response.credits_left;
				},
			} );
		}
	}

	/**
	 * Fetches and sets the recipes that use credits from the server.
	 *
	 * If the site is connected, this function sends a request to the server to get the recipes that use credits. On success, it sets the `recipesUsingCredits` property with the response data.
	 */
	getRecipesUsingCredits() {
		// Check if we should get the recipes
		if ( UncannyAutomatorBackend.hasSiteConnected ) {
			// Do a request to get the credits left
			fetchData( {
				isWordPressREST: true,
				url: `${UncannyAutomatorBackend.rest.url}/get-recipes-using-credits/`,
				onSuccess: ( response ) => {
					// Set the recipes
					this.recipesUsingCredits = response.recipes;
				},
			} );
		}
	}

	/**
	 * Gets the DOM elements related to the credits section on the Dashboard page.
	 *
	 * @return {Object} - An object containing the DOM elements for the credits left box, progress bar, quantity, total, and the recipes using credits content.
	 */
	get $elements() {
		return {
			creditsLeft: {
				box: document.getElementById( 'uap-dashboard-credits-left' ),
				progressBar: document.getElementById( 'uap-dashboard-credits-left-progress-bar' ),
				left: document.getElementById( 'uap-dashboard-credits-left-quantity' ),
				total: document.getElementById( 'uap-dashboard-credits-left-total' ),
			},
			recipesUsingCredits: {
				content: document.getElementById( 'uap-dashboard-credits-recipes-content' ),
			},
		};
	}

	/**
	 * Gets the total credits.
	 *
	 * @return {Number} - The total number of credits.
	 */
	get totalCredits() {
		return this.data.totalCredits;
	}

	/**
	 * Sets the total credits.
	 *
	 * @param {Number} [totalCredits=0] - The total number of credits.
	 */
	set totalCredits( totalCredits = 0 ) {
		// Save data
		this.data.totalCredits = totalCredits;

		// Check if the DOM element is defined
		if ( isDefined( this.$elements.creditsLeft.total ) ) {
			// Try to format the number
			try {
				// Update the UI
				this.$elements.creditsLeft.total.innerText = new Intl.NumberFormat().format( totalCredits );
			} catch ( e ) {
				// Update the UI
				this.$elements.creditsLeft.total.innerText = totalCredits;
			}
		}
	}

	/**
	 * Gets the credits left.
	 *
	 * @return {Number} - The number of credits left.
	 */
	get creditsLeft() {
		return this.data.creditsLeft;
	}

	/**
	 * Sets the credits left.
	 *
	 * @param {Number} [creditsLeft=0] - The number of credits left.
	 */
	set creditsLeft( creditsLeft = 0 ) {
		// Update the UI
		// Start by getting the percentage of used credits
		const percentageUsedCredits = ( creditsLeft / this.totalCredits ) * 100;

		// Set the percentage in the progress bar
		this.$elements.creditsLeft.progressBar.style.width = `${ percentageUsedCredits }%`;

		// Check if the DOM element is defined
		if ( isDefined( this.$elements.creditsLeft.left ) ) {
			// Try to format the number
			try {
				// Set number
				this.$elements.creditsLeft.left.innerText = new Intl.NumberFormat().format( creditsLeft );
			} catch ( e ) {
				// Set number
				this.$elements.creditsLeft.left.innerText = creditsLeft;
			}
		}
	}

	/**
	 * Sets the recipes using credits.
	 *
	 * @param {Array} [recipes=[]] - An array of recipes that use credits.
	 */
	set recipesUsingCredits( recipes = [] ) {
		// Check if there are recipes
		if ( recipes.length > 0 ) {
			// Create array with the LI elements of the list of recipes
			const layoutRecipes = [];

			recipes.forEach( ( recipe ) => {
				// Add LI element with the recipe link
				layoutRecipes.push( `
					<tr>
						<td class="uap-dashboard-credits-recipes-table-cell--recipe-name">
							<a href="${ recipe.url }" target="_blank">
								${ recipe.title }
							</a>
						</td>
						<td class="uap-dashboard-credits-recipes-table-cell--allowed-completions">
							${
								recipe.type == 'user'
									? `<div class="uap-dashboard-credits-recipes-table-allowed-completions--per-user">${UncannyAutomatorBackend.i18n.credits.recipesUsingCredits.table.perUser.replace(
											'%1$s',
											`<span>${
												recipe.times_per_user == false || recipe.times_per_user == -1
													? UncannyAutomatorBackend.i18n.credits.recipesUsingCredits.table
															.unlimited
													: recipe.times_per_user
											}</span>`
										) }</div>`
									: ''
							}

							<div class="uap-dashboard-credits-recipes-table-allowed-completions--total">${ UncannyAutomatorBackend.i18n.credits.recipesUsingCredits.table.total.replace(
								'%1$s',
								`<span>${
									recipe.allowed_completions_total == false || recipe.allowed_completions_total == -1
										? UncannyAutomatorBackend.i18n.credits.recipesUsingCredits.table.unlimited
										: recipe.allowed_completions_total
								}</span>`
							) }</div>
						</td>
						<td class="uap-dashboard-credits-recipes-table-cell--completed-runs">
							${ recipe.completed_runs }
						</td>
					</tr>
				` );
			} );

			// Add it to the UI
			this.$elements.recipesUsingCredits.content.innerHTML = `
				<div class="uap-dashboard-box-content-scroll">
					<table class="uap-table">
						<thead>
							<tr>
								<th class="uap-dashboard-credits-recipes-table-cell--recipe-name">
									${ UncannyAutomatorBackend.i18n.credits.recipesUsingCredits.table.recipe }
								</th>
								<th class="uap-dashboard-credits-recipes-table-cell--allowed-completions">
									${ UncannyAutomatorBackend.i18n.credits.recipesUsingCredits.table.completionsAllowed }
								</th>
								<th class="uap-dashboard-credits-recipes-table-cell--completed-runs">
									${ UncannyAutomatorBackend.i18n.credits.recipesUsingCredits.table.completedRuns }
								</th>
							</tr>
						</thead>
						<tbody>
							${ layoutRecipes.join( '' ) }
						</tbody>
					</table>
				</div>
			`;
		} else {
			// Add the "no recipes" message
			this.$elements.recipesUsingCredits.content.innerHTML = `
				<div class="uap-dashboard-credits-recipes__no-recipes">
					<span class="uap-text-secondary">
						<span class="uap-icon uap-icon--info-circle"></span> ${ UncannyAutomatorBackend.i18n.credits.recipesUsingCredits.noRecipes }
					</span>
				</div>
			`;
		}
	}
}
